import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Home,
  Building2,
  BedDouble,
  HardHat,
  CalendarIcon,
  Clock,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  Phone,
  CreditCard,
  Wallet
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import axios from "axios";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceTypes = [
  { id: "residential", name: "Residential", icon: Home, description: "Home cleaning services" },
  { id: "commercial", name: "Commercial", icon: Building2, description: "Office & business cleaning" },
  { id: "airbnb", name: "Airbnb", icon: BedDouble, description: "Vacation rental turnover" },
  { id: "post-renovation", name: "Post-Renovation", icon: HardHat, description: "Construction cleanup" }
];

const serviceTiers = {
  residential: [
    { id: "standard", name: "Standard Clean", price: "From $120" },
    { id: "deep", name: "Deep Clean", price: "From $200" },
    { id: "move", name: "Move In/Out", price: "From $300" }
  ],
  commercial: [
    { id: "basic", name: "Basic Office", price: "From $150" },
    { id: "professional", name: "Professional", price: "From $250" },
    { id: "enterprise", name: "Enterprise", price: "Custom Quote" }
  ],
  airbnb: [
    { id: "quick", name: "Quick Turnaround", price: "From $100" },
    { id: "full", name: "Full Service", price: "From $150" },
    { id: "premium", name: "Premium Host", price: "From $200" }
  ],
  "post-renovation": [
    { id: "light", name: "Light Renovation", price: "From $250" },
    { id: "standard", name: "Standard Renovation", price: "From $400" },
    { id: "major", name: "Major Construction", price: "Custom Quote" }
  ]
};

const timeSlots = [
  "8:00 AM", "9:00 AM", "10:00 AM", "11:00 AM",
  "12:00 PM", "1:00 PM", "2:00 PM", "3:00 PM",
  "4:00 PM", "5:00 PM"
];

const gtaCities = [
  "Toronto", "North York", "Scarborough", "Etobicoke",
  "Mississauga", "Brampton", "Richmond Hill", "Markham",
  "Vaughan", "Oakville", "Burlington", "Ajax", "Pickering"
];

export default function BookingPage() {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [bookingId, setBookingId] = useState("");
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  
  const [formData, setFormData] = useState({
    service_type: "",
    service_tier: "",
    date: null,
    time: "",
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    postal_code: "",
    property_type: "house",
    bedrooms: "",
    bathrooms: "",
    square_feet: "",
    special_requests: ""
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDateSelect = (date) => {
    setFormData(prev => ({ ...prev, date }));
  };

  const nextStep = () => {
    if (step === 1 && !formData.service_type) {
      toast.error("Please select a service type");
      return;
    }
    if (step === 1 && !formData.service_tier) {
      toast.error("Please select a service tier");
      return;
    }
    if (step === 2 && !formData.date) {
      toast.error("Please select a date");
      return;
    }
    if (step === 2 && !formData.time) {
      toast.error("Please select a time");
      return;
    }
    setStep(prev => prev + 1);
  };

  const prevStep = () => {
    setStep(prev => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.address || !formData.city) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        ...formData,
        date: formData.date ? format(formData.date, "yyyy-MM-dd") : "",
        bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : null,
        bathrooms: formData.bathrooms ? parseInt(formData.bathrooms) : null,
        square_feet: formData.square_feet ? parseInt(formData.square_feet) : null
      };

      const response = await axios.post(`${API}/bookings`, payload);
      setBookingId(response.data.id);
      setIsComplete(true);
      toast.success("Booking submitted successfully!");
    } catch (error) {
      console.error("Error submitting booking:", error);
      toast.error("Failed to submit booking. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePayDeposit = async () => {
    setIsProcessingPayment(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/payments/checkout`, {
        package_id: "deposit",
        booking_id: bookingId,
        origin_url: originUrl
      });
      
      // Redirect to Stripe Checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Error creating checkout session:", error);
      toast.error("Failed to process payment. Please try again or call us.");
    } finally {
      setIsProcessingPayment(false);
    }
  };

  if (isComplete) {
    return (
      <div data-testid="booking-page" className="bg-[#FAFAF9] min-h-screen pt-32 pb-20">
        <div className="container mx-auto px-4 md:px-8 max-w-2xl">
          <div className="bg-white p-12 rounded-2xl shadow-xl border-t-4 border-[#064E3B] text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#F0FDF4] flex items-center justify-center">
              <CheckCircle2 className="w-10 h-10 text-[#064E3B]" />
            </div>
            <h1 className="text-3xl font-bold text-[#0C0A09] mb-4">Booking Submitted!</h1>
            <p className="text-[#78716C] mb-8">
              Thank you for choosing Toronto Premium Cleaning. To confirm your appointment, please pay the $50 deposit below.
            </p>
            <div className="bg-[#F0FDF4] p-6 rounded-xl mb-8">
              <p className="text-sm text-[#78716C] mb-2">Booking Reference</p>
              <p className="text-2xl font-mono font-bold text-[#064E3B]">{bookingId.slice(0, 8).toUpperCase()}</p>
            </div>
            <div className="text-left space-y-3 p-6 bg-[#FAFAF9] rounded-xl mb-8">
              <div className="flex justify-between">
                <span className="text-[#78716C]">Service:</span>
                <span className="font-medium text-[#0C0A09] capitalize">{formData.service_type}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#78716C]">Date:</span>
                <span className="font-medium text-[#0C0A09]">{formData.date ? format(formData.date, "MMMM d, yyyy") : ""}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#78716C]">Time:</span>
                <span className="font-medium text-[#0C0A09]">{formData.time}</span>
              </div>
            </div>

            {/* Payment Section */}
            <div className="bg-[#064E3B] p-6 rounded-xl mb-8 text-white">
              <h3 className="text-xl font-semibold mb-2">Secure Your Booking</h3>
              <p className="text-white/80 text-sm mb-4">
                Pay a $50 deposit to confirm your appointment. The remaining balance is due after service completion.
              </p>
              <div className="flex items-center justify-center gap-3 mb-4">
                <CreditCard className="w-6 h-6" />
                <Wallet className="w-6 h-6" />
                <img src="https://upload.wikimedia.org/wikipedia/commons/f/f2/Google_Pay_Logo.svg" alt="Google Pay" className="h-6 w-auto brightness-0 invert" />
                <img src="https://upload.wikimedia.org/wikipedia/commons/b/b0/Apple_Pay_logo.svg" alt="Apple Pay" className="h-6 w-auto brightness-0 invert" />
              </div>
              <Button
                onClick={handlePayDeposit}
                disabled={isProcessingPayment}
                className="w-full bg-[#C0A062] hover:bg-[#B08D55] text-white rounded-full py-6 text-lg font-medium"
                data-testid="pay-deposit-btn"
              >
                {isProcessingPayment ? "Processing..." : "Pay $50 Deposit"}
              </Button>
            </div>

            <p className="text-[#78716C] text-sm mb-6">
              Prefer to pay later? We'll contact you to confirm your booking. Call us at (647) 870-0445.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/">
                <Button className="bg-[#064E3B] hover:bg-[#065F46] text-white rounded-full px-8 py-6">
                  Back to Home
                </Button>
              </Link>
              <a href="tel:+16478700445">
                <Button variant="outline" className="border-[#064E3B] text-[#064E3B] hover:bg-[#064E3B] hover:text-white rounded-full px-8 py-6">
                  <Phone className="mr-2 w-4 h-4" />
                  Call Us
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="booking-page" className="bg-[#FAFAF9] min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-12 bg-[#064E3B]">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-sm uppercase tracking-widest font-semibold text-[#C0A062] mb-4">
              Book Online
            </p>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight leading-[1.1] text-white mb-4">
              Schedule Your Cleaning
            </h1>
            <p className="text-lg text-white/80">
              Book your professional cleaning service in just a few steps.
            </p>
          </div>
        </div>
      </section>

      {/* Progress Bar */}
      <div className="bg-white border-b border-[#E7E5E4] sticky top-20 z-40">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl py-4">
          <div className="flex items-center justify-between">
            {[
              { num: 1, label: "Service" },
              { num: 2, label: "Date & Time" },
              { num: 3, label: "Details" },
              { num: 4, label: "Confirm" }
            ].map((s, i) => (
              <div key={s.num} className="flex items-center">
                <div className={`flex items-center ${i > 0 ? "ml-2" : ""}`}>
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                      step >= s.num
                        ? "bg-[#064E3B] text-white"
                        : "bg-[#E7E5E4] text-[#78716C]"
                    }`}
                  >
                    {step > s.num ? <CheckCircle2 className="w-5 h-5" /> : s.num}
                  </div>
                  <span className={`ml-2 text-sm font-medium hidden sm:inline ${
                    step >= s.num ? "text-[#064E3B]" : "text-[#78716C]"
                  }`}>
                    {s.label}
                  </span>
                </div>
                {i < 3 && (
                  <div className={`w-8 sm:w-16 h-0.5 ml-2 ${
                    step > s.num ? "bg-[#064E3B]" : "bg-[#E7E5E4]"
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Booking Form */}
      <section className="py-12 md:py-20">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <form onSubmit={handleSubmit}>
            {/* Step 1: Service Selection */}
            {step === 1 && (
              <div className="space-y-8" data-testid="step-1">
                <div>
                  <h2 className="text-2xl font-semibold text-[#0C0A09] mb-6">
                    Select Your Service
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {serviceTypes.map((service) => (
                      <button
                        key={service.id}
                        type="button"
                        onClick={() => {
                          handleSelectChange("service_type", service.id);
                          handleSelectChange("service_tier", "");
                        }}
                        data-testid={`service-${service.id}`}
                        className={`p-6 rounded-xl border-2 text-left transition-all ${
                          formData.service_type === service.id
                            ? "border-[#064E3B] bg-[#F0FDF4]"
                            : "border-[#E7E5E4] hover:border-[#064E3B]/50 bg-white"
                        }`}
                      >
                        <service.icon className={`w-8 h-8 mb-3 ${
                          formData.service_type === service.id ? "text-[#064E3B]" : "text-[#78716C]"
                        }`} />
                        <h3 className="font-semibold text-[#0C0A09]">{service.name}</h3>
                        <p className="text-sm text-[#78716C]">{service.description}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {formData.service_type && (
                  <div>
                    <h2 className="text-2xl font-semibold text-[#0C0A09] mb-6">
                      Choose Your Package
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {serviceTiers[formData.service_type]?.map((tier) => (
                        <button
                          key={tier.id}
                          type="button"
                          onClick={() => handleSelectChange("service_tier", tier.id)}
                          data-testid={`tier-${tier.id}`}
                          className={`p-6 rounded-xl border-2 text-left transition-all ${
                            formData.service_tier === tier.id
                              ? "border-[#C0A062] bg-[#FFFBF5]"
                              : "border-[#E7E5E4] hover:border-[#C0A062]/50 bg-white"
                          }`}
                        >
                          <h3 className="font-semibold text-[#0C0A09] mb-1">{tier.name}</h3>
                          <p className="text-[#C0A062] font-medium">{tier.price}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Date & Time */}
            {step === 2 && (
              <div className="space-y-8" data-testid="step-2">
                <div>
                  <h2 className="text-2xl font-semibold text-[#0C0A09] mb-6">
                    Select Date
                  </h2>
                  <div className="bg-white p-6 rounded-xl border border-[#E7E5E4] inline-block">
                    <Calendar
                      mode="single"
                      selected={formData.date}
                      onSelect={handleDateSelect}
                      disabled={(date) => date < new Date() || date < new Date("1900-01-01")}
                      className="rounded-md"
                      data-testid="calendar"
                    />
                  </div>
                </div>

                <div>
                  <h2 className="text-2xl font-semibold text-[#0C0A09] mb-6">
                    Select Time
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    {timeSlots.map((time) => (
                      <button
                        key={time}
                        type="button"
                        onClick={() => handleSelectChange("time", time)}
                        data-testid={`time-${time.replace(/[:\s]/g, '-')}`}
                        className={`p-4 rounded-lg border-2 text-center transition-all ${
                          formData.time === time
                            ? "border-[#064E3B] bg-[#F0FDF4] text-[#064E3B] font-medium"
                            : "border-[#E7E5E4] hover:border-[#064E3B]/50 bg-white text-[#78716C]"
                        }`}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Property Details */}
            {step === 3 && (
              <div className="space-y-8" data-testid="step-3">
                <div>
                  <h2 className="text-2xl font-semibold text-[#0C0A09] mb-6">
                    Contact Information
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="h-12 bg-white"
                        placeholder="Your full name"
                        data-testid="booking-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="h-12 bg-white"
                        placeholder="your@email.com"
                        data-testid="booking-email"
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="h-12 bg-white"
                        placeholder="(647) 000-0000"
                        data-testid="booking-phone"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h2 className="text-2xl font-semibold text-[#0C0A09] mb-6">
                    Property Address
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="address">Street Address *</Label>
                      <Input
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        required
                        className="h-12 bg-white"
                        placeholder="123 Main Street, Unit 456"
                        data-testid="booking-address"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="city">City *</Label>
                      <Select onValueChange={(v) => handleSelectChange("city", v)} value={formData.city}>
                        <SelectTrigger className="h-12 bg-white" data-testid="booking-city">
                          <SelectValue placeholder="Select city" />
                        </SelectTrigger>
                        <SelectContent>
                          {gtaCities.map((city) => (
                            <SelectItem key={city} value={city}>{city}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="postal_code">Postal Code</Label>
                      <Input
                        id="postal_code"
                        name="postal_code"
                        value={formData.postal_code}
                        onChange={handleInputChange}
                        className="h-12 bg-white"
                        placeholder="M5V 1A1"
                        data-testid="booking-postal"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h2 className="text-2xl font-semibold text-[#0C0A09] mb-6">
                    Property Details
                  </h2>
                  <div className="space-y-6">
                    <div className="space-y-3">
                      <Label>Property Type</Label>
                      <RadioGroup
                        value={formData.property_type}
                        onValueChange={(v) => handleSelectChange("property_type", v)}
                        className="flex flex-wrap gap-4"
                      >
                        {["house", "condo", "apartment", "townhouse", "office"].map((type) => (
                          <div key={type} className="flex items-center space-x-2">
                            <RadioGroupItem value={type} id={type} />
                            <Label htmlFor={type} className="capitalize cursor-pointer">{type}</Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="bedrooms">Bedrooms</Label>
                        <Select onValueChange={(v) => handleSelectChange("bedrooms", v)} value={formData.bedrooms}>
                          <SelectTrigger className="h-12 bg-white">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {[1, 2, 3, 4, 5, "6+"].map((n) => (
                              <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="bathrooms">Bathrooms</Label>
                        <Select onValueChange={(v) => handleSelectChange("bathrooms", v)} value={formData.bathrooms}>
                          <SelectTrigger className="h-12 bg-white">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {[1, 2, 3, 4, "5+"].map((n) => (
                              <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="square_feet">Square Feet (approx)</Label>
                        <Input
                          id="square_feet"
                          name="square_feet"
                          value={formData.square_feet}
                          onChange={handleInputChange}
                          className="h-12 bg-white"
                          placeholder="e.g., 1500"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="special_requests">Special Requests or Notes</Label>
                      <Textarea
                        id="special_requests"
                        name="special_requests"
                        value={formData.special_requests}
                        onChange={handleInputChange}
                        rows={4}
                        className="bg-white resize-none"
                        placeholder="Any special instructions, areas of focus, or things we should know..."
                        data-testid="booking-notes"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Review & Confirm */}
            {step === 4 && (
              <div className="space-y-8" data-testid="step-4">
                <h2 className="text-2xl font-semibold text-[#0C0A09] mb-6">
                  Review Your Booking
                </h2>

                <div className="bg-white p-8 rounded-2xl border border-[#E7E5E4] space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-sm font-medium text-[#78716C] mb-2">Service</h3>
                      <p className="text-lg font-semibold text-[#0C0A09] capitalize">
                        {formData.service_type?.replace('-', ' ')}
                      </p>
                      <p className="text-[#064E3B] capitalize">{formData.service_tier} Package</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-[#78716C] mb-2">Date & Time</h3>
                      <p className="text-lg font-semibold text-[#0C0A09]">
                        {formData.date ? format(formData.date, "EEEE, MMMM d, yyyy") : ""}
                      </p>
                      <p className="text-[#064E3B]">{formData.time}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-[#78716C] mb-2">Contact</h3>
                      <p className="font-medium text-[#0C0A09]">{formData.name}</p>
                      <p className="text-[#78716C]">{formData.email}</p>
                      <p className="text-[#78716C]">{formData.phone}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-[#78716C] mb-2">Address</h3>
                      <p className="font-medium text-[#0C0A09]">{formData.address}</p>
                      <p className="text-[#78716C]">{formData.city}, {formData.postal_code}</p>
                    </div>
                  </div>

                  {formData.special_requests && (
                    <div className="pt-6 border-t border-[#E7E5E4]">
                      <h3 className="text-sm font-medium text-[#78716C] mb-2">Special Requests</h3>
                      <p className="text-[#0C0A09]">{formData.special_requests}</p>
                    </div>
                  )}
                </div>

                <div className="bg-[#F0FDF4] p-6 rounded-xl border border-[#064E3B]/20">
                  <p className="text-[#064E3B] text-sm">
                    <strong>Note:</strong> Final pricing will be confirmed after we assess your property. 
                    A team member will contact you within a few hours to confirm your booking and provide a final quote.
                  </p>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-12 pt-8 border-t border-[#E7E5E4]">
              {step > 1 ? (
                <Button
                  type="button"
                  onClick={prevStep}
                  variant="outline"
                  className="border-[#064E3B] text-[#064E3B] hover:bg-[#064E3B] hover:text-white rounded-full px-8 py-6"
                  data-testid="prev-step"
                >
                  <ArrowLeft className="mr-2 w-4 h-4" />
                  Previous
                </Button>
              ) : (
                <div />
              )}

              {step < 4 ? (
                <Button
                  type="button"
                  onClick={nextStep}
                  className="bg-[#064E3B] hover:bg-[#065F46] text-white rounded-full px-8 py-6"
                  data-testid="next-step"
                >
                  Continue
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-[#C0A062] hover:bg-[#B08D55] text-white rounded-full px-8 py-6"
                  data-testid="submit-booking"
                >
                  {isSubmitting ? "Submitting..." : "Confirm Booking"}
                  <CheckCircle2 className="ml-2 w-4 h-4" />
                </Button>
              )}
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
